import { reportRequestFailure } from './request-error-reporting.js';
import { HttpRequestError } from '../shared/request-error.js';
import { object, literal, string } from 'zod/mini';
import '../styles/google-signin-button.css';

interface GoogleIdentity {
  initialize(options: {
    client_id: string;
    nonce: string;
    use_fedcm_for_button: boolean;
    callback: (response: { credential: string; state?: string }) => void;
  }): void;
  renderButton(container: HTMLElement, options: {
    type: 'standard'; theme: 'outline'; size: 'large'; text: 'continue_with';
    width: number; locale: string; state: string; click_listener: () => void;
  }): void;
}
interface SigninOptions {
  fields: () => Record<string, string | boolean>;
  started: () => void;
  success: () => void | Promise<void>;
  failed: () => void;
}
const configSchema = object({ enabled: literal(true), clientId: string(), nonce: string() });
type IdentityState = { status: 'empty' } | { status: 'loading'; completion: Promise<GoogleIdentity> } | { status: 'ready'; value: GoogleIdentity };
type SigninAttempt = { state: string; options: SigninOptions; fields: Record<string, string | boolean> };
type Submission = { status: 'submitting' | 'abandoned'; attempt: SigninAttempt };
let identityState: IdentityState = { status: 'empty' };
let attemptState: { status: 'idle' } | { status: 'selected'; attempt: SigninAttempt } | Submission = { status: 'idle' };

function identityClient(): Promise<GoogleIdentity> {
  if (identityState.status === 'ready') return Promise.resolve(identityState.value);
  if (identityState.status === 'loading') return identityState.completion;
  const completion = loadIdentity().then(value => {
    if (identityState === loading) identityState = { status: 'ready', value };
    return value;
  }, error => { if (identityState === loading) identityState = { status: 'empty' }; throw error; });
  const loading: IdentityState = { status: 'loading', completion };
  identityState = loading;
  return completion;
}

function googleIdentity(): GoogleIdentity | undefined {
  // SAFETY: Google's loaded Identity Services script supplies this documented API.
  return (window as typeof window & { google?: { accounts?: { id?: GoogleIdentity } } }).google?.accounts?.id;
}

async function loadIdentity(): Promise<GoogleIdentity> {
  const response = await fetch('/api/auth/google/identity', { cache: 'no-store', credentials: 'same-origin' });
  if (!response.ok) throw new HttpRequestError('Google sign-in configuration unavailable', response, 'auth.google.config');
  const config = configSchema.parse(await response.json());
  if (!googleIdentity()) {
    await new Promise<void>((resolve, reject) => {
      const src = `https://accounts.google.com/gsi/client?hl=${encodeURIComponent(document.documentElement.lang || 'en')}`;
      const present = document.querySelector<HTMLScriptElement>('script[src="https://accounts.google.com/gsi/client"], script[src^="https://accounts.google.com/gsi/client?"]');
      const script = present ?? document.createElement('script');
      const cleanup = () => { clearTimeout(timeout); script.removeEventListener('load', loaded); script.removeEventListener('error', failed); };
      const loaded = () => { cleanup(); script.dataset.loaded = 'true'; resolve(); };
      const failed = () => { cleanup(); if (!present) script.remove(); reject(new Error('Google sign-in script unavailable')); };
      const timeout = setTimeout(failed, 10_000);
      script.addEventListener('load', loaded, { once: true });
      script.addEventListener('error', failed, { once: true });
      if (!present) { script.src = src; script.async = true; document.head.appendChild(script); }
    });
  }
  const identity = googleIdentity();
  if (!identity) throw new Error('Google sign-in unavailable');
  identity.initialize({ client_id: config.clientId, nonce: config.nonce, use_fedcm_for_button: true, callback: response => { void signIn(response.credential, response.state); } });
  return identity;
}

async function signIn(credential: string, state: string | undefined) {
  if (attemptState.status !== 'selected' || attemptState.attempt.state !== state) return;
  const attempt = attemptState.attempt;
  const submission: Submission = { status: 'submitting', attempt };
  attemptState = submission;
  try {
    const response = await fetch('/api/auth/google/identity', {
      method: 'POST', credentials: 'same-origin', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ credential, ...attempt.fields }),
    });
    if (!response.ok) throw await HttpRequestError.fromResponse('Google sign-in failed', response, 'auth.google.signin');
    identityState = { status: 'empty' };
    if (attemptState === submission && submission.status === 'submitting') await attempt.options.success();
  } catch (cause) {
    reportRequestFailure(cause, 'auth.google.signin');
    if (attemptState === submission && submission.status === 'submitting') attempt.options.failed();
  } finally {
    if (attemptState === submission) attemptState = { status: 'idle' };
  }
}

export function mountGoogleSignIn(container: HTMLElement, options: SigninOptions): () => void {
  const state = crypto.randomUUID();
  type MountState = { status: 'ready' | 'loading'; width: number } | { status: 'disposed' };
  let mount: MountState = { status: 'ready', width: 0 };
  const contentObserver = new ResizeObserver(([entry]) => {
    if (entry) container.style.height = `${entry.contentRect.height * 1.2}px`;
  });
  const handlers: SigninOptions = { ...options, failed: () => {
    observer.disconnect();
    contentObserver.disconnect();
    container.style.removeProperty('height');
    container.replaceChildren();
    delete container.dataset.ready;
    options.failed();
  } };
  const observer = new ResizeObserver(async () => {
    const nextWidth = Math.min(400, Math.floor(container.getBoundingClientRect().width));
    if (mount.status !== 'ready' || nextWidth < 200 || nextWidth === mount.width) return;
    const loading: MountState = { status: 'loading', width: mount.width };
    mount = loading;
    try {
      const identity = await identityClient();
      if (mount !== loading || !container.isConnected || container.getBoundingClientRect().width < 200) return;
      const button = document.createElement('div');
      button.className = 'google-signin-content gs:w-[calc(100%_/_1.2)] gs:[margin-inline:auto] gs:[transform:scale(1.2)] gs:[transform-origin:top_center]';
      contentObserver.disconnect();
      container.replaceChildren(button);
      contentObserver.observe(button);
      identity.renderButton(button, {
        type: 'standard', theme: 'outline', size: 'large', text: 'continue_with',
        width: Math.floor(nextWidth / 1.2), locale: document.documentElement.lang || 'en', state,
        click_listener: () => {
          if (attemptState.status === 'submitting' || attemptState.status === 'abandoned' || mount.status === 'disposed') return;
          attemptState = { status: 'selected', attempt: { state, options: handlers, fields: options.fields() } };
          options.started();
        },
      });
      loading.width = nextWidth;
      container.dataset.ready = 'true';
    } catch (cause) {
      reportRequestFailure(cause, 'auth.google.config');
      contentObserver.disconnect();
      container.style.removeProperty('height');
      container.replaceChildren();
      delete container.dataset.ready;
    } finally { if (mount === loading) mount = { status: 'ready', width: loading.width }; }
  });
  observer.observe(container);
  return () => {
    mount = { status: 'disposed' };
    observer.disconnect();
    contentObserver.disconnect();
    container.style.removeProperty('height');
    if (attemptState.status === 'selected' && attemptState.attempt.options === handlers) attemptState = { status: 'idle' };
    else if (attemptState.status === 'submitting' && attemptState.attempt.options === handlers) attemptState.status = 'abandoned';
    container.replaceChildren();
    delete container.dataset.ready;
  };
}
